import { useI18n } from './i18n';
import { motion } from 'framer-motion';
import { Target, Users, Search } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const pillarIcons = [Target, Users, Search];

export default function LandingAbout() {
  const { t } = useI18n();

  return (
    <>
      {/* Header */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" animate="visible" className="max-w-2xl">
            <motion.span
              variants={fadeUp}
              custom={0}
              className="inline-block text-xs font-semibold tracking-[0.2em] text-[#34B2C8] mb-4"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {t.about.overline}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-3xl sm:text-4xl lg:text-5xl font-light text-slate-900 mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t.about.title}
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-base text-slate-500 leading-relaxed">
              {t.about.intro}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mb-16"
          >
            <motion.h2
              variants={fadeUp}
              custom={0}
              className="text-2xl sm:text-3xl font-medium text-slate-900 mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {t.about.methodology.title}
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-base text-slate-500 max-w-xl">
              {t.about.methodology.desc}
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.about.methodology.pillars.map((pillar, i) => {
              const Icon = pillarIcons[i];
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="group p-8 rounded-2xl border border-slate-100 hover:border-[#34B2C8]/30 bg-slate-50/50 hover:bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#34B2C8]/5"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#34B2C8]/10 flex items-center justify-center mb-6 group-hover:bg-[#34B2C8]/20 transition-colors duration-300">
                    <Icon size={22} className="text-[#34B2C8]" />
                  </div>
                  <h3
                    className="text-xl font-medium text-slate-900 mb-3"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{pillar.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Notre équipe */}
      <section className="py-24 bg-slate-50 border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.h2
              variants={fadeUp} custom={0}
              className="text-2xl sm:text-3xl font-medium text-slate-900 mb-12"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Notre équipe
            </motion.h2>
          </motion.div>

          <div className="space-y-8">
            {/* Thierry Assonion */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}
              className="bg-white rounded-2xl border border-slate-200 p-8"
            >
              <div className="flex items-start gap-6 mb-4">
                <img src="/images/team/thierry.jpg" alt="Thierry Assonion" className="w-24 h-24 rounded-xl object-cover flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Thierry Assonion
                  </h3>
                  <span className="text-sm font-medium text-[#34B2C8]">Président</span>
                </div>
              </div>
              <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
                <p>
                  Ancien officier supérieur de l'armée de terre, breveté de l'école de guerre, expert du renseignement, Thierry Assonion est le fondateur du cabinet de conseil Anticiper. Depuis 2020, il apporte son expertise à des entreprises en développement sur de nouveaux marchés ou espaces géographiques ainsi qu'à des établissements de recherche comme l'INRIA ou l'Université Technologique de Compiègne.
                </p>
                <p>
                  Expert de l'espace post-soviétique et concepteur de simulateurs d'analyse stratégique, il élabore également des formations à la vigilance, à la coopération inter-fonctions et à la gestion des signaux faibles.
                </p>
                <p>
                  Thierry Assonion est également enseignant à l'École de Guerre Économique dans le domaine du Renseignement Humain.
                </p>
              </div>
            </motion.div>

            {/* Thibaut Milewski */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}
              className="bg-white rounded-2xl border border-slate-200 p-8"
            >
              <div className="flex items-start gap-6 mb-4">
                <img src="/images/team/thibaut.jpg" alt="Thibaut Milewski" className="w-24 h-24 rounded-xl object-cover flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Thibaut Milewski
                  </h3>
                  <span className="text-sm font-medium text-[#34B2C8]">Competitive Intelligence Manager</span>
                </div>
              </div>
              <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
                <p>
                  Diplômé d'un premier master de l'École Supérieure du Commerce Extérieur, puis d'un second master d'Études Stratégiques, Sécurité et Politique de Défense de l'école des Hautes Études Internationales et Politiques, Thibaut Milewski débute sa carrière au sein du Centre Interarmées de Concepts, de Doctrines et d'Expérimentations.
                </p>
                <p>
                  Spécialiste des dispositifs de simulation et d'exploitation de données représentatives du renseignement, Thibaut Milewski collabore avec Anticiper depuis 2021, notamment pour l'élaboration et l'animation des formations.
                </p>
              </div>
            </motion.div>

            {/* Raphaël Dosseur */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2}
              className="bg-white rounded-2xl border border-slate-200 p-8"
            >
              <div className="flex items-start gap-6 mb-4">
                <img src="/images/team/raphael-dosseur.jpg" alt="Raphaël Dosseur" className="w-24 h-24 rounded-xl object-cover flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Raphaël Dosseur
                  </h3>
                  <span className="text-sm font-medium text-[#34B2C8]">Analyste junior</span>
                </div>
              </div>
              <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
                <p>
                  Étudiant en Master Sécurité Défense à l'école des Hautes Études Internationales et Politiques, Raphaël Dosseur participe à la préparation et à l'animation des formations.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </>
  );
}
